/**
 * Capability map for sync profile types.
 * To add support for a new CRM type, add an entry here — no other file needs to change.
 *
 * deprecated: true  → type still accepted by the API but no longer offered for new profiles.
 */
const SYNC_PROFILE_CAPABILITIES = {
  MS_DYNAMICS: {
    label: 'Microsoft Dynamics',
    supportsFieldMappings: true,
    supportsQuoteSync: true,
    supportsSimilaritySearch: true,
  },
  MS_BUSINESS_CENTRAL: {
    label: 'Microsoft Business Central',
    supportsFieldMappings: true,
    supportsQuoteSync: false,
    supportsSimilaritySearch: false,
  },
  WEBHOOK: {
    label: 'Webhook',
    supportsFieldMappings: false,
    supportsQuoteSync: false,
    supportsSimilaritySearch: false,
  },
  // Legacy type — still valid on existing profiles but deprecated; use WEBHOOK for new ones.
  MS_DYNAMICS_CUSTOM: {
    label: 'Microsoft Dynamics Custom',
    supportsFieldMappings: false,
    supportsQuoteSync: false,
    supportsSimilaritySearch: false,
    deprecated: true,
  },
};

/**
 * Valid values for the nullable `targetCrm` field on a sync profile.
 * Used for display purposes (labels, icons) when type=WEBHOOK.
 * The null entry represents "no specific CRM target".
 */
export const TARGET_CRM_OPTIONS = [
  { value: '' }, // represents null/no specific CRM; label supplied by i18n caller
  { label: 'Microsoft Dynamics', value: 'MS_DYNAMICS' },
  { label: 'Microsoft Business Central', value: 'MS_BUSINESS_CENTRAL' },
  { label: 'Zoho', value: 'ZOHO' },
  // Future: SALESFORCE, HUBSPOT
];

/**
 * Returns true if the profile type supports custom field mapping configuration
 * and field-mapping-driven sync.
 */
export function supportsFieldMappings(type) {
  return SYNC_PROFILE_CAPABILITIES[type]?.supportsFieldMappings ?? false;
}

/**
 * Returns true if the profile type supports quote synchronization.
 */
export function supportsQuoteSync(type) {
  return SYNC_PROFILE_CAPABILITIES[type]?.supportsQuoteSync ?? false;
}

/**
 * Returns true if the profile type's external system supports searching for
 * similar/existing opportunities by name. Only Dynamics-family CRMs do; Webhook is a passthrough. The sync dialog
 * uses this to decide whether to pre-fire the similarity-warning lookup on open.
 */
export function supportsSimilaritySearch(type) {
  return SYNC_PROFILE_CAPABILITIES[type]?.supportsSimilaritySearch ?? false;
}

/**
 * Returns the human-readable label for a profile type value.
 * Falls back to the raw value string if the type is unknown.
 */
export function getProfileTypeLabel(type) {
  return SYNC_PROFILE_CAPABILITIES[type]?.label ?? type;
}

/**
 * Returns the ordered list of profile type options for use in select inputs.
 * Deprecated types are excluded so they cannot be chosen for new profiles, but any
 * type listed in `includeDeprecated` is force-included even if marked deprecated
 * (used by the edit dialog to render the type of an existing legacy profile).
 * Deprecated labels are suffixed with " (deprecated)" for visual disambiguation.
 */
export function getProfileTypeOptions(includeDeprecated = []) {
  return Object.entries(SYNC_PROFILE_CAPABILITIES)
    .filter(([value, caps]) => !caps.deprecated || includeDeprecated.includes(value))
    .map(([value, { label, deprecated }]) => ({
      label: deprecated ? `${label} (deprecated)` : label,
      value,
      deprecated: !!deprecated,
    }));
}

/**
 * Filters field mappings to those that should be shown/synced for the current operation.
 * On create: keep ALWAYS + CREATE_ONLY. On update: keep ALWAYS + UPDATE_ONLY.
 */
export function filterFieldMappingsForMode(fieldMappings, isCreate) {
  return (fieldMappings || []).filter((f) => (isCreate ? f.syncMode !== 'UPDATE_ONLY' : f.syncMode !== 'CREATE_ONLY'));
}

function parseFieldConfig(fieldMapping) {
  if (!fieldMapping?.fieldConfig) return {};
  return typeof fieldMapping.fieldConfig === 'string' ? JSON.parse(fieldMapping.fieldConfig) : fieldMapping.fieldConfig;
}

export function buildLookupFieldDefaults(
  fieldMappings,
  { accountQuery = '', accountCreate = null, contactQuery = '', contactCreate = null } = {},
) {
  const defaultQueries = {};
  const createDefaults = {};

  (fieldMappings || [])
    .filter((field) => field.fieldType === 'lookup' && field.valueExpression)
    .forEach((field) => {
      const config = parseFieldConfig(field);

      if (config.lookupType === 'account') {
        if (accountQuery) defaultQueries[field.valueExpression] = accountQuery;
        if (accountCreate) createDefaults[field.valueExpression] = { ...accountCreate };
      }

      if (config.lookupType === 'contact') {
        if (contactQuery) defaultQueries[field.valueExpression] = contactQuery;
        if (contactCreate) createDefaults[field.valueExpression] = { ...contactCreate };
      }
    });

  return { defaultQueries, createDefaults };
}
